import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { ZoneGeometry, ZoneInput, ZoneCategory, ZoneType } from "@/api/v0/zones";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

// Define the form schema with Zod
const zoneFormSchema = z.object({
  name: z.string().min(3, {
    message: "Zone name must be at least 3 characters.",
  }),
  description: z.string().optional(),
  category: z.enum(["restricted", "warning", "information"]),
  color: z.string().regex(/^#([0-9A-F]{3}){1,2}$/i, {
    message: "Please enter a valid hex color code.",
  }),
});

// Infer the form values type from the schema
type ZoneFormValues = z.infer<typeof zoneFormSchema>;

interface ZoneFormProps {
  onSubmit: (data: ZoneInput) => void;
  zoneGeometry: ZoneGeometry;
  geometryType: ZoneType;
  initialValues?: Partial<ZoneFormValues>;
  isSubmitting?: boolean;
}

export function ZoneForm({
  onSubmit,
  zoneGeometry,
  initialValues,
  isSubmitting = false,
}: ZoneFormProps) {
  const [color, setColor] = useState(initialValues?.color || "#FF0000");

  // Initialize the form with react-hook-form
  const form = useForm<ZoneFormValues>({
    resolver: zodResolver(zoneFormSchema),
    defaultValues: {
      name: initialValues?.name || "",
      description: initialValues?.description || "",
      category: initialValues?.category || "restricted",
      color: initialValues?.color || "#FF0000",
    },
  });

  // Handle form submission
  const handleSubmit = (values: ZoneFormValues) => {
    // Ensure the type matches the actual geometry

    // Use the actual geometry type from zoneGeometry instead of the geometryType prop
    const zoneType = zoneGeometry.type as ZoneType;
    
    // Format the geometry based on the actual geometry type
    let formattedGeometry: any;
    
    if (zoneGeometry.type === 'circle') {
      // Circle geometry is already in the correct format
      formattedGeometry = {
        type: 'circle',
        center: (zoneGeometry as any).center,
        radius: (zoneGeometry as any).radius
      };
    } else if (zoneGeometry.type === 'polygon') {
      if (zoneType === 'circle') {
        // If we're drawing a circle but have polygon geometry, convert to circle
        const coordinates = (zoneGeometry as any).coordinates[0];
        let sumLng = 0;
        let sumLat = 0;
        
        // Skip the last point as it's the same as the first in a closed polygon
        const points = coordinates.length > 0 ? coordinates.slice(0, -1) : coordinates;
        
        for (const point of points) {
          sumLng += point[0];
          sumLat += point[1];
        }
        
        const center: [number, number] = [
          sumLng / points.length,
          sumLat / points.length
        ];
        
        // Calculate the radius (distance from center to farthest point)
        let maxDistance = 0;
        for (const point of points) {
          // Simple Euclidean distance for now
          const dx = center[0] - point[0];
          const dy = center[1] - point[1];
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance > maxDistance) {
            maxDistance = distance;
          }
        }
        
        // Convert to meters (approximate)
        const radiusInMeters = Math.round(maxDistance * 111319.9);
        
        formattedGeometry = {
          type: 'circle',
          center: center,
          radius: radiusInMeters
        };
      } else {
        // Keep as polygon
        formattedGeometry = zoneGeometry;
      }
    }
    
    const zoneInput: ZoneInput = {
      ...values,
      geometry: formattedGeometry,
      type: zoneType, // Use the actual geometry type
      category: values.category as ZoneCategory,
    };
    
    onSubmit(zoneInput);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Zone Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter zone name" {...field} />
              </FormControl>
              <FormDescription>
                A descriptive name for this zone.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter zone description"
                  className="min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Optional details about this zone.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Zone Category</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select zone category" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="restricted">Restricted</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="information">Information</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>
                  The category determines how this zone is treated.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="color"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Zone Color</FormLabel>
                <div className="flex items-center gap-2">
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="#FF0000"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        setColor(e.target.value);
                      }}
                    />
                  </FormControl>
                  <div
                    className="w-10 h-10 rounded-md border"
                    style={{ backgroundColor: color }}
                  />
                </div>
                <FormDescription>
                  Color used to display the zone on the map.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save Zone"
          )}
        </Button>
      </form>
    </Form>
  );
}
